import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { motion } from 'framer-motion';
import {
  ThankYouLocationState,
  useSignupConversionTracking
} from 'src/features/register/hooks/useSignupConversionTracking';
import RegisterSuccessMessage from 'src/features/register/ui/RegisterSuccessMessage';
import * as Analytics from 'src/services/analytics/analytics';
import { Logo } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { useMetaPixel, usePageTitle } from 'src/shared/hooks';

/**
 * Dedicated conversion landing page at /register/thank-you.
 *
 * Google Analytics uses a visit to this URL as its conversion criterion.
 * We explicitly fire a GA pageview and the `sign_up` event here because the
 * standard GA init only runs inside PrivateRoute for logged-in users, and a
 * just-registered visitor is not yet authenticated. The `sign_up` event and
 * Google Ads conversion fire once per registration, not on reloads.
 */
const RegisterThankYouPage = () => {
  const { state } = useLocation();
  const { email, openSignUp = true } = (state as ThankYouLocationState) ?? {};

  usePageTitle('Thank you | Cavela');
  useMetaPixel('989168503310664');

  useEffect(() => {
    Analytics.trackAnonymousPageView(ROUTE_PATHS.REGISTER_THANK_YOU);
  }, []);

  useSignupConversionTracking();

  return (
    <div className="bg-background">
      <div className="bg-white min-h-dvh flex flex-col">
        <div className="grid-cols-2 md:grid flex h-full flex-1 w-full">
          <div className="relative w-full p-10">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ ease: 'easeInOut', duration: 0.6 }}
              className="w-full md:max-w-lg md:mx-auto flex flex-col justify-center h-full"
            >
              <div className="h-8">
                <Logo />
              </div>

              <RegisterSuccessMessage openSignUp={openSignUp} email={email} />
            </motion.div>
          </div>

          <div
            className="hidden items-center justify-center md:flex"
            style={{
              background: 'url(/backgrounds/registerBgGreen.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            <motion.img
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ ease: 'easeInOut', duration: 1 }}
              draggable="false"
              className="w-[60%]"
              alt="ipad"
              src="/backgrounds/register-hero.png"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterThankYouPage;
